import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { getEnv } from '../config';
import { decrypData, encrypData, getEncryptedKey, randomKey, removeKey } from './security';
import { TBFFNames } from '../interfaces';

const { encryption, contextCountry, helpCenterBffURL, contextService } = getEnv();

class AxiosFactory {
  private static headers = {
    'x-flow-country': `${contextCountry}`,
    'x-flow-service': `${contextService}`,
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    Accept: '*/*',
  };

  public static getInstance(type: TBFFNames): AxiosInstance {
    return AxiosFactory.createAxiosInstance(type);
  }

  private static getUrl(type: TBFFNames): string {
    let bffURL;
    switch (type) {
      case 'BFF_HELP_CENTER_URL':
        bffURL = helpCenterBffURL;
        return bffURL;
      default:
        bffURL = '';
        return bffURL;
    }
  }

  private static requestInterceptor(request: AxiosRequestConfig): any {
    const randKey = randomKey();
    const finalRequest = { ...request, data: { payload: request.data } };
    if (encryption) {
      const { payload, key } = encrypData(request, randKey);
      finalRequest.data = { payload };
      finalRequest.headers!['x-flow-tkn'] = getEncryptedKey(key);
      finalRequest.headers!['x-flow-type-key'] = randKey;
    }
    return finalRequest;
  }

  private static responseIntercerptor(response: AxiosResponse): any {
    let responseData;
    try {
      responseData = response.data.payload;
      if (encryption) {
        responseData = decrypData(responseData, response.config.headers['x-flow-type-key']);
        removeKey(response.config.headers['x-flow-type-key']);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Unexpected response error', e);
    }
    return responseData;
  }

  private static responseErrorInterceptor(error: any) {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (encryption) {
      removeKey();
    }

    const objectError = { ...(error?.response?.data ?? error), statusCode: error?.response?.status };
    return Promise.reject(objectError);
  }

  private static createAxiosInstance = (type: TBFFNames): AxiosInstance => {
    const instance = axios.create({
      baseURL: AxiosFactory.getUrl(type),
      timeout: 30000,
      headers: AxiosFactory.headers,
    });
    instance.interceptors.request.use(AxiosFactory.requestInterceptor);
    instance.interceptors.response.use(AxiosFactory.responseIntercerptor, AxiosFactory.responseErrorInterceptor);

    return instance;
  };
}

export default AxiosFactory;
